import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useLingui } from "@lingui/react";
import { useAuthdAPI } from "../api";
import appRoutes from "../constants/appRoutes";
import { Order } from "../constants/types";
import { useErrorToast } from "../showToast";
import { ORDER_NOT_FOUND_TEXT } from "../constants/i18n-strings";
import { Logger } from "../../logger";

export function useOrders() {
  const showErrorToast = useErrorToast();

  const { data, error, mutate } = useAuthdAPI<Order[]>("orders");

  useEffect(() => {
    if (!error) return;
    showErrorToast(error);
    Logger.error(error);
  }, [error, showErrorToast]);

  return {
    orders: data,
    hasOrders: data && data.length !== 0,
    mutateOrders: mutate,
  };
}

export function useOrderWithRedirect(sourceId: string | undefined) {
  const { orders, mutateOrders } = useOrders();
  const [order, setOrder] = useState<Order | undefined>(undefined);
  const router = useRouter();
  const showErrorToast = useErrorToast();
  const { _: i18n } = useLingui();

  useEffect(() => {
    if (!router.isReady || router.pathname !== appRoutes.ORDER_VIEW_PAGE) {
      return;
    }
    if (!orders || !sourceId) {
      return;
    }

    const preferredOrder = orders.find(
      (o) => String(o.source_id) === String(sourceId)
    );

    if (preferredOrder) {
      setOrder(preferredOrder);
    } else {
      router.push(appRoutes.MY_ORDERS);
      showErrorToast(i18n(ORDER_NOT_FOUND_TEXT));
    }
  }, [orders, router, showErrorToast, sourceId, i18n]);

  function mutateOrder(newOrder: Order) {
    setOrder(newOrder);
    mutateOrders(
      orders?.map((order) =>
        order.source_id === newOrder.source_id ? newOrder : order
      )
    );
  }

  return { order, mutateOrder };
}
